/**
 * Form Progress Bar JavaScript
 * Tracks form completion and updates progress bar dynamically
 */

// EmailJS will be loaded via CDN in the HTML file
class FormProgress {
  constructor(formSelector, progressBarSelector, progressTextSelector) {
    this.form = document.querySelector(formSelector);
    this.progressBar = document.querySelector(progressBarSelector);
    this.progressText = document.querySelector(progressTextSelector);

    if (!this.form || !this.progressBar || !this.progressText) {
      console.warn("Form progress elements not found");
      return;
    }

    this.requiredFields = this.form.querySelectorAll(
      "input[required], select[required]",
    );
    this.allFields = this.form.querySelectorAll("input, select, textarea");
    this.init();
  }

  init() {
    // Add event listeners to all form fields
    this.allFields.forEach((field) => {
      field.addEventListener("input", () => this.updateProgress());
      field.addEventListener("change", () => this.updateProgress());
      field.addEventListener("blur", () => this.updateProgress());
    });

    // Initial progress calculation
    this.updateProgress();
  }

  updateProgress() {
    const totalFields = this.requiredFields.length;
    let completedFields = 0;

    // Count completed required fields
    this.requiredFields.forEach((field) => {
      if (this.isFieldValid(field)) {
        completedFields++;
      }
    });

    // Calculate progress percentage
    const progressPercentage = Math.round(
      (completedFields / totalFields) * 100,
    );

    // Update progress bar
    this.updateProgressBar(progressPercentage);

    // Update progress text
    this.updateProgressText(progressPercentage, completedFields, totalFields);

    // Add completion effects
  }

  isFieldValid(field) {
    if (field.type === "email") {
      return field.value.trim() !== "" && this.isValidEmail(field.value);
    }

    if (field.type === "tel") {
      return field.value.trim() !== "" && this.isValidPhone(field.value);
    }

    return field.value.trim() !== "";
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  }

  updateProgressBar(percentage) {
    // Smooth animation
    this.progressBar.style.width = `${percentage}%`;

    // Add visual feedback based on progress
    this.progressBar.className = this.progressBar.className.replace(
      /tw:from-\w+-\d+/g,
      "",
    );
    this.progressBar.className = this.progressBar.className.replace(
      /tw:to-\w+-\d+/g,
      "",
    );

    if (percentage < 25) {
      this.progressBar.classList.add("tw:from-red-500", "tw:to-red-600");
    } else if (percentage < 50) {
      this.progressBar.classList.add("tw:from-orange-500", "tw:to-orange-600");
    } else if (percentage < 75) {
      this.progressBar.classList.add("tw:from-yellow-500", "tw:to-yellow-600");
    } else if (percentage < 100) {
      this.progressBar.classList.add("tw:from-blue-500", "tw:to-blue-600");
    } else {
      this.progressBar.classList.add("tw:from-green-500", "tw:to-green-600");
    }
  }

  updateProgressText(percentage, completed, total) {
    if (percentage === 100) {
      this.progressText.textContent = "✓  Complete!";
      this.progressText.classList.add("tw:text-green-600", "tw:font-bold");
    } else {
      this.progressText.textContent = `${percentage}% Complete (${completed}/${total} fields)`;
      this.progressText.classList.remove("tw:text-green-600", "tw:font-bold");
    }
  }

  // Method to manually set progress (useful for testing)
  setProgress(percentage) {
    this.updateProgressBar(Math.max(0, Math.min(100, percentage)));
    this.updateProgressText(percentage, 0, this.requiredFields.length);
  }

  // Method to reset progress
  reset() {
    this.updateProgressBar(0);
    this.updateProgressText(0, 0, this.requiredFields.length);
    this.progressBar.classList.remove("tw:animate-pulse");
  }
}

// Enhanced form validation with real-time feedback
class FormValidator {
  constructor(formSelector) {
    this.form = document.querySelector(formSelector);
    if (!this.form) return;

    this.init();
  }

  init() {
    // Add real-time validation to all fields
    const fields = this.form.querySelectorAll("input, select, textarea");

    fields.forEach((field) => {
      field.addEventListener("blur", () => this.validateField(field));
      field.addEventListener("input", () => this.clearFieldError(field));
    });
  }

  validateField(field) {
    const isValid = this.isFieldValid(field);
    const fieldContainer = field.closest(".form-field");

    if (!isValid && field.hasAttribute("required")) {
      // this.showFieldError(field, fieldContainer);
    } else {
      // this.showFieldSuccess(field, fieldContainer);
    }

    return isValid;
  }

  isFieldValid(field) {
    if (!field.hasAttribute("required") && field.value.trim() === "") {
      return true; // Optional empty fields are valid
    }

    if (field.hasAttribute("required") && field.value.trim() === "") {
      return false;
    }

    if (field.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(field.value);
    }

    if (field.type === "tel") {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
      return phoneRegex.test(field.value.replace(/\s/g, ""));
    }

    return field.value.trim() !== "";
  }

  showFieldError(field, container) {
    field.classList.add("tw:ring-2", "tw:ring-red-500", "tw:bg-red-50");
    field.classList.remove("tw:ring-green-500", "tw:bg-green-50");

    if (container) {
      container.classList.add("tw:animate-shake");
      setTimeout(() => container.classList.remove("tw:animate-shake"), 500);
    }
  }

  showFieldSuccess(field, container) {
    if (field.value.trim() !== "") {
      field.classList.add("tw:ring-2", "tw:ring-green-500", "tw:bg-green-50");
      field.classList.remove("tw:ring-red-500", "tw:bg-red-50");
    }
  }

  clearFieldError(field) {
    field.classList.remove(
      "tw:ring-red-500",
      "tw:bg-red-50",
      "tw:ring-green-500",
      "tw:bg-green-50",
    );
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // Initialize progress tracker
  const progressTracker = new FormProgress(
    "#contactForm",
    "#progressBar",
    "#progressText",
  );

  // Initialize form validator
  const formValidator = new FormValidator("#contactForm");

  // Handle form submission
  const form = document.getElementById("contactForm");
  if (form) {
    form.addEventListener("submit", function (e) {
      e.preventDefault();

      // Show loading state
      const submitButton = form.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;

      submitButton.textContent = "SUBMITTING...";
      submitButton.disabled = true;
      submitButton.classList.add("tw:opacity-75");

      // Simulate form submission (replace with actual submission logic)

      // Collect form data
      const formData = new FormData(form);
      const templateParams = {};
      for (let [key, value] of formData.entries()) {
        templateParams[key] = value;
      }
      
      // Send email using EmailJS
      emailjs
        .send("service_kgkl43o", "template_gerkzo4", templateParams)
        .then((response) => afterSuccessFullSubmission())
        .catch((error) => {
          console.error("Form submission failed:", error);
          // Reset button state on error
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.classList.remove("tw:opacity-75");
          alert("Failed to send message. Please try again.");
        });

      function afterSuccessFullSubmission() {
        // Show success animation
        const successAnimation = document.getElementById("successAnimation");
        if (successAnimation) {
          successAnimation.classList.remove("tw:hidden");
          successAnimation.classList.add("tw:flex");
        }
        form.reset();
        submitButton.textContent = originalText;
        submitButton.disabled = false;
        submitButton.classList.remove("tw:opacity-75");

        setTimeout(() => {
          successAnimation.classList.add("tw:hidden");
          successAnimation.classList.remove("tw:flex");
        }, 4000);
      }
    });
  }
});

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = { FormProgress, FormValidator };
}
